import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Tooltip, IconButton, TextField, Snackbar, Alert, CircularProgress,
    Pagination, FormControl, Select, MenuItem
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon, Payment as PaymentIcon,
    Edit as EditIcon, RotateLeft as RotateLeftIcon, Refresh as RefreshIcon
} from "@mui/icons-material";
import { getData, postData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

// --- Helper Functions ---
const formatMonth = (monthString) => {
    const [year, month] = monthString.split("-");
    const date = new Date(year, month - 1, 1);
    return date.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
    });
};

const getShortMonth = (fullMonth) =>
    new Date(`${fullMonth} 1, 2020`).toLocaleString("en-US", {
        month: "short",
    });

function PayrollRecordsTab({ onNavigateToRunPayrollProcess }) {
    // --- State ---
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [employeeRows, setEmployeeRows] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editRowId, setEditRowId] = useState(null);
    const [editLop, setEditLop] = useState("");
    const [finalizeLoadingMonth, setFinalizeLoadingMonth] = useState(null);
    const [paymentLoadingMonth, setPaymentLoadingMonth] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showMessage = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getPayrollMonthsDropdown);
            const months =
                response?.statusCode === 200 && response.result?.months
                    ? [...response.result.months].sort((a, b) => new Date(b) - new Date(a))
                    : [];

            const results = await Promise.all(
                months.map(async (monthString) => {
                    const formattedMonth = formatMonth(monthString);
                    const [monthName, year] = formattedMonth.split(" ");
                    const shortMonth = getShortMonth(monthName);
                    const res = await getData(API_ENDPOINTS.getBatchOverview(year, shortMonth));
                    const rows = res?.result || [];
                    const lockedCount = rows.filter((r) => r.locked === true).length;
                    const finalizedCount = rows.filter((r) => r.finalized === true).length;
                    const paidCount = rows.filter((r) => r.payed_flag === true).length;

                    return {
                        month: formattedMonth,
                        year,
                        shortMonth,
                        rows,
                        totalEmployees: rows.length,
                        lockedCount,
                        finalizedCount,
                        paidCount,
                        locked: rows.length > 0 && lockedCount === rows.length,
                        finalized: rows.length > 0 && finalizedCount === rows.length,
                        paid: rows.length > 0 && paidCount === rows.length,
                    };
                })
            );

            setRecords(results);
            setSelectedMonth((prevSelected) => {
                if (results.length > 0) {
                    const currentMonthName = prevSelected?.month;
                    const updatedSelection = currentMonthName
                        ? results.find(r => r.month === currentMonthName)
                        : results[0];

                    setEmployeeRows(updatedSelection?.rows || results[0].rows || []);
                    return updatedSelection || results[0];
                }
                return null;
            });

            return results;
        } catch (err) {
            showMessage("Unable to load payroll records.", "error");
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMonthDetails = (record) => {
        setSelectedMonth(record);
        setEmployeeRows(record?.rows || []);
        setEditRowId(null);
        setSearchQuery("");
        setPage(1);
    };

    // --- Actions ---
    const handleFinalize = async (record) => {
        try {
            setFinalizeLoadingMonth(record.month);
            if (!record.locked) {
                const lockPayload = { month: record.shortMonth, year: Number(record.year) };
                const lockRes = await putData(API_ENDPOINTS.updateBatchPayrollLock, lockPayload);
                if (lockRes?.statusCode !== 200) {
                    showMessage(lockRes?.message || "Failed to lock payroll before finalizing.", "error");
                    return;
                }
            }

            const finalizePayload = {
                month: record.shortMonth,
                year: Number(record.year),
                finalized: true,
            };
            const resetRes = await putData(API_ENDPOINTS.updateBatchPayrollLockAndFinalize, finalizePayload);

            if (resetRes?.statusCode !== 200) {
                showMessage(resetRes?.message || "Failed to finalize payroll.", "error");
            } else {
                showMessage("Payroll finalized successfully.");
                await fetchRecords();
            }
        } catch (err) {
            showMessage("Something went wrong while finalizing payroll.", "error");
        } finally {
            setFinalizeLoadingMonth(null);
        }
    };

    const handlePayment = async (record) => {
        if (!record.locked || !record.finalized) {
            showMessage("Ensure payroll is locked and finalized before payment.", "warning");
            return;
        }

        try {
            setPaymentLoadingMonth(record.month);
            const payoutRes = await postData(API_ENDPOINTS.disbursePayrollPayouts, { month: record.shortMonth, year: record.year });

            if (payoutRes?.statusCode === 200) {
                showMessage(payoutRes.message || `Payment completed for ${record.month}.`);
                await fetchRecords();
            } else {
                showMessage(payoutRes?.message || "Failed to process payment.", "error");
            }
        } catch (err) {
            showMessage("Something went wrong while processing payment.", "error");
        } finally {
            setPaymentLoadingMonth(null);
        }
    };

    const handleEditEmployee = (row) => {
        if (row?.finalized === true) {
            showMessage("Cannot edit after finalization.", "warning");
            return;
        }
        setEditRowId(row.id);
        setEditLop(String(row.lopCount ?? 0));
    };

    const saveEmployeeEdit = async (row) => {
        const parsed = Number(editLop) || 0;
        try {
            const res = await putData(API_ENDPOINTS.updateBatchPayrollById, { id: row.id, lopCount: parsed });
            if (res?.statusCode === 200) {
                showMessage(res.message || "Payroll detail updated.");
                setEditRowId(null);
                await fetchRecords();
            } else {
                showMessage(res?.message || "Failed to update.", "error");
            }
        } catch (err) {
            showMessage("Error updating payroll record", "error");
        }
    };

    const handleDeleteMonth = async (record) => {
        if (!record) return;
        try {
            const payload = {
                month: record.shortMonth,
                year: Number(record.year),
                locked: false,
                finalized: false,
            };
            const resetRes = await putData(API_ENDPOINTS.updateBatchPayrollLockAndFinalize, payload);
            if (resetRes?.statusCode === 200) {
                showMessage(`Payroll reset for ${record.month}`, "info");
                await fetchRecords();
            } else {
                showMessage(resetRes?.message || "Failed to reset payroll.", "error");
            }
        } catch (err) {
            showMessage("Failed to reset payroll.", "error");
        }
    };

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    // --- Search & Pagination Calculations ---
    const query = searchQuery.trim().toLowerCase();
    const filteredEmployees = query
        ? employeeRows.filter(
            (row) =>
                String(row.empId || "").toLowerCase().includes(query) ||
                String(row.name || "").toLowerCase().includes(query)
        )
        : employeeRows;

    const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / rowsPerPage));
    const paginatedEmployees = filteredEmployees.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <Box>
            <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="h5" fontWeight="bold">Payroll Records</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Finalize payroll records first, then proceed to payment.
                    </Typography>
                </Box>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchRecords}
                    disabled={loading}
                    variant="outlined"
                >
                    Refresh
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell>Employees</TableCell>
                            <TableCell>Locked</TableCell>
                            <TableCell>Finalized</TableCell>
                            <TableCell>Paid</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                            </TableRow>
                        ) : (
                            records.map((rec) => (
                                <TableRow
                                    key={rec.month}
                                    hover
                                    selected={selectedMonth?.month === rec.month}
                                    onClick={() => fetchMonthDetails(rec)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>{rec.month}</TableCell>
                                    <TableCell>{rec.totalEmployees}</TableCell>
                                    <TableCell>{rec.locked ? "Yes" : "No"}</TableCell>
                                    <TableCell>{rec.finalized ? "Yes" : "No"}</TableCell>
                                    <TableCell>{rec.paid ? "Yes" : "No"}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Finalize (locks payroll)">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleFinalize(rec); }}
                                                    disabled={finalizeLoadingMonth === rec.month || rec.totalEmployees === 0 || rec.finalized}
                                                >
                                                    {finalizeLoadingMonth === rec.month ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Disburse Payment">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handlePayment(rec); }}
                                                    disabled={paymentLoadingMonth === rec.month || !rec.finalized || rec.paid}
                                                >
                                                    {paymentLoadingMonth === rec.month ? <CircularProgress size={18} /> : <PaymentIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Reset payroll">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteMonth(rec); }}
                                                    disabled={rec.paid}
                                                >
                                                    <RotateLeftIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedMonth && selectedMonth.locked ? (
                <Box mt={4}>
                    <Typography variant="h6" mb={2}>{selectedMonth.month} Details</Typography>
                    <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                        <TextField
                            label="Search employee"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                            sx={{ width: 300 }}
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">Rows per page:</Typography>
                            <FormControl size="small">
                                <Select
                                    value={rowsPerPage}
                                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                                >
                                    {[5, 10, 25, 50].map((count) => <MenuItem key={count} value={count}>{count}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Emp ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>LOP</TableCell>
                                    <TableCell>Gross</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedEmployees.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.empId}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>
                                            {editRowId === row.id ? (
                                                <TextField
                                                    size="small" type="number" value={editLop}
                                                    onChange={(e) => setEditLop(e.target.value)}
                                                    sx={{ width: 80 }}
                                                />
                                            ) : (row.lopCount ?? 0)}
                                        </TableCell>
                                        <TableCell>{row.totalGross ?? "-"}</TableCell>
                                        <TableCell align="center">
                                            {editRowId === row.id ? (
                                                <Box>
                                                    <Button size="small" onClick={() => saveEmployeeEdit(row)}>Save</Button>
                                                    <Button size="small" onClick={() => setEditRowId(null)}>Cancel</Button>
                                                </Box>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditEmployee(row)}
                                                    disabled={selectedMonth.finalized}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredEmployees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No employees found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredEmployees.length > 0 && (
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
                        </Box>
                    )}
                </Box>
            ) : selectedMonth && (
                <Box mt={3}>
                    <Alert severity="info" action={
                        <Button color="inherit" size="small" onClick={onNavigateToRunPayrollProcess}>
                            Go to Process Flow
                        </Button>
                    }>
                        Payroll data will be visible once the month is locked in the Process Flow.
                    </Alert>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PayrollRecordsTab;